import * as React from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { InfoAlert } from '@amzn/dvui/dist/Alert'
import { Text } from '@amzn/dvui/dist/Text'
import * as st from './refund.module.scss'
import * as stUtility from '../utilityClasses.module.scss'
import { IDetailPageState } from '../../reducers'
import { sanitizedRequestContextSelector } from '../../selectors/requestContext'
import { triggerRefund } from '../../actions/refund'
import { ModalTrigger } from '@shared/utils/modalTrigger/ModalTrigger'
import { LinkProps } from '@amzn/dvui/dist/Link'
import { PrimeButton } from '@amzn/dvui/dist/Button'
import { CloseableDropList } from '../../utils/closeableDropList'
import { IGetString } from '../../utils/stringUtils'
import { ILogger, withLogger } from '../../context/withLogger'
import { AtfTreatmentProps } from '../../reducers/features'
import * as cn from 'classnames'
import { isTreatment } from '../../utils/utils'
import { Link } from '@amzn/dvui/dist/Link'
import { DpWidgetContext } from '../../utils/dpWidgetContext'

const DROP_LIST_ID = 'av-atf-multiple-refund'

type StateProps = ReturnType<typeof mapStateToProps>
type DispatchProps = ReturnType<typeof mapDispatchToProps>
type OwnProps = {
  titleId: string
} & AtfTreatmentProps
export type Props = StateProps & DispatchProps & OwnProps

const createCancelLinkText = (
  href: string,
  getString: IGetString,
  additionalAttr?: { [index: string]: string }
) => ({
  string: getString('DV_AB_CANCEL_ACCIDENTAL_PURCHASE'),
  attrs: { cancelLink: { href, ...additionalAttr } }
})

type CancelLinkLegacyProps = {
  refundableOrders: WpsRuntime.atv.wps.RefundableOrder[],
  pageTitleId: string,
  getString: IGetString
}

interface CancelLinkLegacyState {
  selectedIdx: number
  selectedLink: string
}

class CancelLinkLegacy extends React.Component<CancelLinkLegacyProps, CancelLinkLegacyState> {
  constructor(props: CancelLinkLegacyProps) {
    super(props)
    this.state = {
      selectedIdx: 0,
      selectedLink: props.refundableOrders[0].singleRefundLink,
    }
  }

  updateSelected = (link: string, idx: number) => {
    this.setState({
      selectedLink: link,
      selectedIdx: idx
    })
  }

  convertRefundOrdersToLinks = (refunds: WpsRuntime.atv.wps.RefundableOrder[]): LinkProps[] =>
    refunds.map((refund, idx) => ({
      text: refund.description,
      isHighlighted: this.state.selectedIdx === idx,
      href: `#${idx}`,
      onClick: (e: React.MouseEvent<HTMLElement> | React.MouseEvent<HTMLAnchorElement>) => {
        this.updateSelected(refund.singleRefundLink, idx)
        e.preventDefault()
      }
    }))

  addReturnUrl = (link: string) => `${link}&return_to=/dp/${this.props.pageTitleId}`

  render() {
    const {refundableOrders, getString} = this.props
    if (refundableOrders.length === 1) {
      return <Text string={createCancelLinkText(refundableOrders[0].singleRefundLink, getString)}/>
    } else {
      const header = <strong> {getString('DV_RBB_CANCEL_PURCH_MODAL_HEADER')} </strong>

      const content = <>
        <div className={`${st.content}`}> {getString('DV_DP_you_multiple_orders_for_this_title')} </div>
        <div> {getString('DV_DP_choose_order_to_cancel')} </div>
        <div className={`${st.dropList}`} data-automation-id="refund-drop-list">
          <CloseableDropList links={this.convertRefundOrdersToLinks(refundableOrders)} customDropListID={DROP_LIST_ID}/>
        </div>
        <PrimeButton href={this.addReturnUrl(this.state.selectedLink)} className={st.submitButton} data-automation-id="refund-submit-button">
          {getString('DV_RBB_CANCEL_PURCH_MODAL_SUBMIT')}
        </PrimeButton>
      </>

      return (<ModalTrigger
        size='xs'
        className={`${st.modal}`}
        modalHeader={header}
        modalContent={content}
        toggle={({openModalClickHandler}) => (
          <CancelLink
            triggerRefund={openModalClickHandler}
            getString={getString}
          />
        )}
      />)
    }
  }
}

type CancelLink = {
  triggerRefund: (e: React.MouseEvent<HTMLElement>) => void,
  getString: IGetString
}

const CancelLink = ({ triggerRefund, getString }: CancelLink) => {
  const dvString = getString('DV_AB_CANCEL_ACCIDENTAL_PURCHASE')
  const linkStartTag = '{link|cancelLink}'
  const linkEndTag = '{end}'
  const linkStartIndex = dvString.indexOf(linkStartTag)
  const linkEndIndex = dvString.indexOf(linkEndTag)
  const prefix = dvString.substring(0, linkStartIndex)
  const link = dvString.substring(
    linkStartIndex + linkStartTag.length,
    linkEndIndex
  )
  const suffix = dvString.substring(linkEndIndex + linkEndTag.length)
  return (
    <>
      {prefix && <span>{prefix}</span>}
      <Link href='#' onClick={(e: React.MouseEvent<HTMLElement>) => triggerRefund(e)}>
        {link}
      </Link>
      {suffix && <span>{suffix}</span>}
    </>
  )
}

export const ConnectedRefund = ({
                                  refundFragment,
                                  isRetail,
                                  pageTitleId,
                                  triggerRefund,
                                  atfTreatment
                                }: Props) => {
  const { getString } = React.useContext(DpWidgetContext)
  if (
    !refundFragment ||
    !refundFragment.refundableOrders ||
    refundFragment.refundableOrders.length === 0
  ) {
    return null
  }

  return (
    <div className={cn(st.refundLink, stUtility.fullWidth, 'refund-alert', {[st.refundLinkWrapper]: isTreatment(atfTreatment, 'T1', 'T2', 'T3')})}>
      <InfoAlert {...(isTreatment(atfTreatment, 'T1', 'T2', 'T3') && {className: st.refundAlert})}>
        {isRetail ?
          <CancelLinkLegacy
            refundableOrders={refundFragment.refundableOrders}
            pageTitleId={pageTitleId}
            getString={getString}
          />
          : <CancelLink
            triggerRefund={triggerRefund}
            getString={getString}
          />}
      </InfoAlert>
    </div>
  )
}

const mapStateToProps = (state: IDetailPageState, { titleId }: OwnProps & ILogger) => ({
  refundFragment: state.refund.fragments[titleId],
  isRetail: sanitizedRequestContextSelector(state).isRetail,
  pageTitleId: titleId
})

const mapDispatchToProps = (
  dispatch: Dispatch,
  { logger, titleId }: ILogger & OwnProps
) => ({
  triggerRefund: () => triggerRefund(dispatch, logger, titleId)
})

export const Refund = withLogger(connect(mapStateToProps, mapDispatchToProps)(ConnectedRefund))